import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import styles from './ApplicationConfirmPage.module.css';

const secondsDiff = (expiresAtIso) => {
  try {
    const expires = new Date(expiresAtIso).getTime();
    const now = Date.now();
    return Math.max(0, Math.floor((expires - now) / 1000));
  } catch {
    return 0;
  }
};

const formatMMSS = (sec) => {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

const isValidStripeCheckoutUrl = (rawUrl) => {
  try {
    const parsed = new URL(rawUrl);
    return parsed.protocol === 'https:' && parsed.hostname === 'checkout.stripe.com';
  } catch {
    return false;
  }
};

const ApplicationConfirmPage = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reservationId, setReservationId] = useState(null);
  const [expiresAt, setExpiresAt] = useState(null);
  const [items, setItems] = useState([]);
  const [baseTotal, setBaseTotal] = useState(0);
  const [feeBearer, setFeeBearer] = useState('organizer');
  const [buyerFeeAmount, setBuyerFeeAmount] = useState(0);
  const [total, setTotal] = useState(0);
  const [remaining, setRemaining] = useState(0);

  const selectedItems = useMemo(() => state?.items || [], [state]);

  useEffect(() => {
    if (!selectedItems.length) {
      // 選択が無ければイベント詳細へ戻す
      navigate(`/events/${id}`);
      return;
    }

    const hold = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await axios.post(
          `${process.env.REACT_APP_API_URL}/reservations/hold`,
          { items: selectedItems },
          {}
        );
        setReservationId(res.data.reservation_id);
        setExpiresAt(res.data.expires_at);
        setItems(res.data.items || []);
        setBaseTotal(res.data.base_total ?? res.data.total ?? 0);
        setFeeBearer(res.data.fee_bearer || 'organizer');
        setBuyerFeeAmount(res.data.buyer_fee_amount || 0);
        setTotal(res.data.total || 0);
        setRemaining(secondsDiff(res.data.expires_at));
      } catch (e) {
        console.error(e);
        setError('在庫の確保に失敗しました。やり直してください。');
      } finally {
        setLoading(false);
      }
    };

    hold();
  }, [id, navigate, selectedItems]);

  useEffect(() => {
    if (!expiresAt) return;
    const tick = () => setRemaining((prev) => (prev > 0 ? prev - 1 : 0));
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [expiresAt]);

  const handlePurchase = async () => {
    if (!reservationId) return;
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/purchase`,
        { reservation_id: reservationId }
      );
      const url = res.data.url;
      // Stripe Checkout URL のみ許可（オープンリダイレクト対策）
      if (!isValidStripeCheckoutUrl(url)) {
        alert('無効なリダイレクト先です。');
        return;
      }
      window.location.href = url;
    } catch (e) {
      console.error(e);
      alert('購入処理に失敗しました');
    }
  };

  const disabled = loading || !!error || remaining <= 0;

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        <h1 className={styles.heading}>申し込み確認</h1>

        {loading && (
          <div className={styles.loadingWrap}>
            <div className={styles.spinner} />
            <span>在庫を確保しています…</span>
          </div>
        )}

        {error && (
          <div className={styles.errorBanner}>{error}</div>
        )}

        {!loading && !error && (
          <>
            {/* タイマー */}
            <div className={styles.timerBanner}>
              <span className={styles.timerLabel}><img src="/icons/clock.svg" alt="タイマー" style={{width: '1.2em', verticalAlign: 'middle', marginRight: '0.2em'}} />確保期限（この時間内に購入を完了してください）</span>
              {remaining > 0 ? (
                <span className={styles.timerValue}>{formatMMSS(remaining)}</span>
              ) : (
                <span className={styles.timerExpired}>00:00</span>
              )}
            </div>

            {/* 明細 */}
            <p className={styles.sectionTitle}>申し込み内容</p>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>種別</th>
                  <th>価格タイプ</th>
                  <th>単価</th>
                  <th>枚数</th>
                  <th>小計</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, idx) => (
                  <tr key={idx}>
                    <td>{it.label}</td>
                    <td>{it.price_type ?? '—'}</td>
                    <td>¥{it.unit_amount.toLocaleString()}</td>
                    <td>{it.quantity}</td>
                    <td>¥{it.sub_total.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* 合計 */}
            <div className={styles.tableFooter}>
              <div className={styles.subtotalRow}>
                <span>チケット合計</span>
                <span>¥{baseTotal.toLocaleString()}</span>
              </div>
              {feeBearer === 'buyer' && buyerFeeAmount > 0 && (
                <div className={styles.feeRow}>
                  <span>システム利用料（7%）</span>
                  <span>¥{buyerFeeAmount.toLocaleString()}</span>
                </div>
              )}
              <div className={styles.totalRow}>
                <span>お支払い合計</span>
                <span className={styles.totalAmount}>¥{total.toLocaleString()}</span>
              </div>
            </div>

            <p className={styles.notice}>
              ※ 購入ボタンを押すとStripeの決済ページに移動します。<br />
              ※ 確保期限内に購入を完了してください。期限を過ぎると在庫が解放されます。
            </p>

            {/* ボタン */}
            <div className={styles.actions}>
              <button className={styles.backBtn} onClick={() => navigate(-1)}>
                戻る
              </button>
              <button
                className={styles.purchaseBtn}
                onClick={handlePurchase}
                disabled={disabled}
              >
                購入する →
              </button>
            </div>

            {remaining <= 0 && (
              <p className={styles.expiredText}>
                ⚠ 予約の有効期限が切れました。最初からやり直してください。
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ApplicationConfirmPage;
